import axios from "axios";
import { API_BASE_URL } from "../../config";
import { authHeaders } from "../utils/session";

/**
 * Lead Assigned Master — which employees receive a service's leads, and how many
 * of them each may take this month.
 *
 * The mapping used to be edited through the employee record; it has its own
 * table now, so nothing here touches an employee's profile.
 */

const jsonHeaders = () => ({
  ...authHeaders(),
  "Content-Type": "application/json",
});

const apiError = (err, fallback) => {
  const status = err?.response?.status;
  const data = err?.response?.data;
  if (status === 403) {
    return new Error(data?.message || "Only an admin or superadmin can manage lead assignments.");
  }
  const firstFieldError = data?.errors?.[Object.keys(data?.errors || {})[0]]?.[0];
  return new Error(firstFieldError || data?.message || data?.error || fallback);
};

/**
 * Every active service comes back, including the ones with an empty roster —
 * that is what makes an empty service addable instead of invisible.
 * @returns {{ services: Array, quotaPeriod: string }}
 */
export const fetchLeadAssignments = async (params = {}) => {
  try {
    const res = await axios.get(`${API_BASE_URL}/lead-assignments`, {
      headers: jsonHeaders(),
      params,
    });
    const body = res.data || {};
    return {
      services: Array.isArray(body.data) ? body.data : [],
      quotaPeriod: body.quota_period || "monthly",
    };
  } catch (err) {
    throw apiError(err, "Failed to load lead assignments");
  }
};

/**
 * Replaces one service's whole roster: anyone not in `employees` loses this
 * service. Add and edit are the same call for that reason.
 *
 * @param {Array<{user_id:number, quota:number|null}>} employees
 *        quota — null is unlimited, 0 is paused, >0 is a monthly limit.
 */
export const saveServiceRoster = async (serviceId, employees) => {
  try {
    const res = await axios.post(
      `${API_BASE_URL}/lead-assignments`,
      { service_id: serviceId, employees },
      { headers: jsonHeaders() },
    );
    const body = res.data || {};
    if (body.status === 422 || body.status === false) {
      throw new Error(body.message || "This roster could not be saved");
    }
    return body.data || body;
  } catch (err) {
    if (err instanceof Error && !err.response) throw err;
    throw apiError(err, "This roster could not be saved");
  }
};

/** Takes one employee off one service; their other services are untouched. */
export const removeFromService = async (serviceId, userId) => {
  try {
    const res = await axios.delete(`${API_BASE_URL}/lead-assignments/${serviceId}/${userId}`, {
      headers: jsonHeaders(),
    });
    return res.data?.message || "Employee removed";
  } catch (err) {
    throw apiError(err, "Could not remove this employee");
  }
};

export const clearServiceRoster = async (serviceId) => {
  try {
    const res = await axios.delete(`${API_BASE_URL}/lead-assignments/${serviceId}`, {
      headers: jsonHeaders(),
    });
    return res.data?.message || "Service cleared";
  } catch (err) {
    throw apiError(err, "Could not clear this service");
  }
};

/**
 * The current roster as a spreadsheet: row per service, column per employee
 * email, cell = quota.
 *
 * Downloading before editing is the whole safety story — an import replaces each
 * service's roster wholesale, so starting from live data is what stops a blank
 * column from silently unassigning someone.
 */
export const exportLeadAssignments = async () => {
  try {
    /* `import-format` is the route the backend actually registers, and it
       streams the current-state matrix — the same file an export would give, so
       one endpoint serves both buttons. */
    const res = await axios.get(`${API_BASE_URL}/lead-assignments/import-format`, {
      headers: { ...authHeaders() },
      responseType: "blob",
    });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = "lead_assignments.xlsx";
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  } catch (err) {
    /* On a blob request the error body is a Blob too, so the API's own message
       has to be read out of it before it can be shown. */
    const blob = err?.response?.data;
    if (blob instanceof Blob) {
      try {
        const parsed = JSON.parse(await blob.text());
        throw new Error(parsed.message || "The export failed");
      } catch (parseErr) {
        if (parseErr instanceof Error && parseErr.message !== "The export failed") {
          throw new Error("The export failed");
        }
        throw parseErr;
      }
    }
    throw apiError(err, "The export failed");
  }
};

/**
 * @param {File} file the edited spreadsheet
 * @param {boolean} createMissing create services named in the sheet but absent
 *        from the database — off by default, because a typo would create junk.
 * @returns the import summary (counts, skipped rows, per-cell errors)
 */
export const importLeadAssignments = async (file, createMissing = false) => {
  try {
    const fd = new FormData();
    fd.append("file", file);
    if (createMissing) fd.append("create_missing_services", "true");
    const res = await axios.post(`${API_BASE_URL}/lead-assignments/import`, fd, {
      // The browser sets the multipart boundary; only auth is ours to add.
      headers: { ...authHeaders() },
    });
    const body = res.data || {};
    if (body.status === 422 || body.status === false) {
      throw new Error(body.message || "The file could not be imported");
    }
    return body.summary || body.data || body;
  } catch (err) {
    if (err instanceof Error && !err.response) throw err;
    throw apiError(err, "The file could not be imported");
  }
};

/** Everyone, for the "add employee" picker. */
export const fetchAllEmployees = async () => {
  try {
    const res = await axios.get(`${API_BASE_URL}/get-internal-user-list`, {
      headers: jsonHeaders(),
      params: { all: 1 },
    });
    const list = res.data?.data;
    return Array.isArray(list) ? list : [];
  } catch (err) {
    throw apiError(err, "Failed to load employees");
  }
};
